import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import {
  Shield,
  ArrowLeft,
  Building2,
  Mail,
  Phone,
  MapPin,
  Users,
  Calendar,
  Clock,
  CheckCircle2,
  XCircle,
  AlertCircle,
  Save,
  Loader2
} from 'lucide-react';
import axios from 'axios';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminBusinessDetail = () => {
  const navigate = useNavigate();
  const { businessId } = useParams();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [subscriptionNotes, setSubscriptionNotes] = useState('');
  const [expiresAt, setExpiresAt] = useState('');

  const getAuthHeader = () => {
    const token = localStorage.getItem('slotmate_admin_token');
    return { Authorization: `Bearer ${token}` };
  };

  const fetchBusiness = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/admin/businesses/${businessId}`, {
        headers: getAuthHeader()
      });
      setData(response.data);
      setSubscriptionStatus(response.data.business.subscription_status || 'trial');
      setSubscriptionNotes(response.data.business.subscription_notes || '');
      setExpiresAt(response.data.business.subscription_expires_at || '');
    } catch (error) {
      console.error('Failed to fetch business:', error);
      if (error.response?.status === 404) {
        toast.error('Business not found');
        navigate('/admin/dashboard');
      } else if (error.response?.status === 403 || error.response?.status === 401) {
        toast.error('Session expired');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  }, [businessId, navigate]);

  useEffect(() => {
    const token = localStorage.getItem('slotmate_admin_token');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchBusiness();
  }, [navigate, fetchBusiness]);

  const handleSaveSubscription = async () => {
    setSaving(true);
    try {
      await axios.put(
        `${API_URL}/admin/businesses/${businessId}/subscription`,
        {
          subscription_status: subscriptionStatus,
          subscription_notes: subscriptionNotes,
          subscription_expires_at: expiresAt || null
        },
        { headers: getAuthHeader() }
      );
      toast.success('Subscription updated successfully');
      fetchBusiness();
    } catch (error) {
      console.error('Failed to update subscription:', error);
      toast.error(error.response?.data?.detail || 'Failed to update subscription');
    } finally {
      setSaving(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'active':
        return <CheckCircle2 className="w-5 h-5 text-emerald-400" />;
      case 'expired':
        return <XCircle className="w-5 h-5 text-red-400" />;
      default:
        return <AlertCircle className="w-5 h-5 text-amber-400" />;
    }
  };

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
      trial: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
      expired: 'bg-red-500/20 text-red-400 border-red-500/30'
    };
    return variants[status] || variants.trial;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="text-white/60 animate-pulse">Loading business details...</div>
      </div>
    );
  }

  if (!data) {
    return null;
  }

  const { business, owner, staff, services, stats } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="bg-white/5 backdrop-blur-xl border-b border-white/10 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/admin/dashboard')}
              className="text-white/60 hover:text-white hover:bg-white/10"
              data-testid="back-btn"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back
            </Button>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center">
              <Shield className="w-4 h-4 text-white" />
            </div>
            <span className="text-white/60 text-sm">Admin Portal</span>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Business Header */}
        <div className="bg-white/5 backdrop-blur-xl rounded-xl p-6 border border-white/10 mb-6">
          <div className="flex flex-col md:flex-row md:items-start justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-16 h-16 rounded-xl bg-gradient-to-br from-purple-500 to-indigo-600 flex items-center justify-center text-white text-2xl font-bold shadow-lg shadow-purple-500/30">
                {business.name?.charAt(0) || 'B'}
              </div>
              <div>
                <div className="flex items-center gap-3 mb-1">
                  <h1 className="text-2xl font-bold text-white">{business.name}</h1>
                  <Badge className={getStatusBadge(business.subscription_status || 'trial')}>
                    {business.subscription_status || 'trial'}
                  </Badge>
                </div>
                <p className="text-white/40 text-sm mb-2">{business.description || 'No description'}</p>
                <div className="flex flex-wrap items-center gap-4 text-sm text-white/60">
                  <span className="flex items-center gap-1">
                    <Mail className="w-4 h-4" />
                    {business.email}
                  </span>
                  {business.phone && (
                    <span className="flex items-center gap-1">
                      <Phone className="w-4 h-4" />
                      {business.phone}
                    </span>
                  )}
                  {business.address && (
                    <span className="flex items-center gap-1">
                      <MapPin className="w-4 h-4" />
                      {business.address}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <div className="text-sm text-white/40">
              Created: {new Date(business.created_at).toLocaleDateString()}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Left Column - Business Info */}
          <div className="lg:col-span-2 space-y-6">
            {/* Stats Cards */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <StatCard icon={Calendar} label="Appointments" value={stats.total_appointments} />
              <StatCard icon={CheckCircle2} label="Completed" value={stats.completed_appointments} />
              <StatCard icon={Users} label="Staff" value={stats.total_staff} />
              <StatCard icon={Building2} label="Services" value={stats.total_services} />
            </div>

            {/* Owner Info */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Business Owner
              </h3>
              {owner ? (
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-purple-500/20 flex items-center justify-center text-purple-400 font-semibold">
                    {owner.name?.charAt(0) || owner.email?.charAt(0) || 'O'}
                  </div>
                  <div>
                    <p className="text-white font-medium">{owner.name || 'No name'}</p>
                    <p className="text-white/40 text-sm">{owner.email}</p>
                  </div>
                </div>
              ) : (
                <p className="text-white/40">No owner information</p>
              )}
            </div>

            {/* Staff List */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Users className="w-5 h-5" />
                Staff Members ({staff.length})
              </h3>
              {staff.length === 0 ? (
                <p className="text-white/40 text-sm">No staff members</p>
              ) : (
                <div className="space-y-3">
                  {staff.map((member) => (
                    <div key={member.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center text-blue-400 text-sm font-medium">
                          {member.name?.charAt(0) || 'S'}
                        </div>
                        <div>
                          <p className="text-white text-sm">{member.name || 'Unnamed'}</p>
                          <p className="text-white/40 text-xs">{member.email}</p>
                        </div>
                      </div>
                      <Badge className={member.is_active ? 'bg-emerald-500/20 text-emerald-400' : 'bg-red-500/20 text-red-400'}>
                        {member.is_active ? 'Active' : 'Inactive'}
                      </Badge>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Services List */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                <Clock className="w-5 h-5" />
                Services ({services.length})
              </h3>
              {services.length === 0 ? (
                <p className="text-white/40 text-sm">No services</p>
              ) : (
                <div className="space-y-2">
                  {services.slice(0, 5).map((service) => (
                    <div key={service.id} className="flex items-center justify-between p-3 bg-white/5 rounded-lg">
                      <div>
                        <p className="text-white text-sm">{service.name}</p>
                        <p className="text-white/40 text-xs">{service.duration} min</p>
                      </div>
                      <div className="text-right">
                        <p className="text-white font-medium">${service.price}</p>
                        {!service.is_active && (
                          <Badge className="bg-red-500/20 text-red-400 text-xs">Inactive</Badge>
                        )}
                      </div>
                    </div>
                  ))}
                  {services.length > 5 && (
                    <p className="text-white/40 text-sm text-center pt-2">
                      +{services.length - 5} more services
                    </p>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Subscription Management */}
          <div className="space-y-6">
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
              <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
                {getStatusIcon(subscriptionStatus)}
                Subscription Management
              </h3>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-white/60">Status</Label>
                  <div className="grid grid-cols-3 gap-2">
                    {['active', 'trial', 'expired'].map((status) => (
                      <button
                        key={status}
                        onClick={() => setSubscriptionStatus(status)}
                        className={`px-3 py-2 rounded-lg text-sm font-medium transition-all ${
                          subscriptionStatus === status
                            ? status === 'active'
                              ? 'bg-emerald-500 text-white'
                              : status === 'trial'
                              ? 'bg-amber-500 text-white'
                              : 'bg-red-500 text-white'
                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                        }`}
                        data-testid={`status-${status}-btn`}
                      >
                        {status.charAt(0).toUpperCase() + status.slice(1)}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60">Expires At (Optional)</Label>
                  <input
                    type="date"
                    value={expiresAt ? expiresAt.split('T')[0] : ''}
                    onChange={(e) => setExpiresAt(e.target.value)}
                    className="w-full px-3 py-2 rounded-lg bg-white/10 border border-white/20 text-white text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                    data-testid="expires-at-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label className="text-white/60">Admin Notes</Label>
                  <Textarea
                    value={subscriptionNotes}
                    onChange={(e) => setSubscriptionNotes(e.target.value)}
                    placeholder="Internal notes about this subscription..."
                    className="bg-white/10 border-white/20 text-white placeholder:text-white/40 min-h-[100px]"
                    data-testid="notes-textarea"
                  />
                </div>

                <Button
                  onClick={handleSaveSubscription}
                  disabled={saving}
                  className="w-full bg-gradient-to-r from-purple-500 to-indigo-600 hover:from-purple-600 hover:to-indigo-700"
                  data-testid="save-subscription-btn"
                >
                  {saving ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4 mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>

            {/* Quick Stats */}
            <div className="bg-white/5 backdrop-blur-xl rounded-xl p-5 border border-white/10">
              <h3 className="text-white font-semibold mb-4">Quick Stats</h3>
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-sm">Total Customers</span>
                  <span className="text-white font-medium">{stats.total_customers}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-sm">Cancelled Bookings</span>
                  <span className="text-white font-medium">{stats.cancelled_appointments}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-white/60 text-sm">Business Slug</span>
                  <span className="text-white/40 text-sm font-mono">{business.slug}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value }) => (
  <div className="bg-white/5 backdrop-blur-xl rounded-xl p-4 border border-white/10">
    <div className="flex items-center gap-2 text-white/40 text-sm mb-1">
      <Icon className="w-4 h-4" />
      {label}
    </div>
    <div className="text-2xl font-bold text-white">{value}</div>
  </div>
);

export default AdminBusinessDetail;
